import { Badge, Pagination, Table, Text } from "@mantine/core";
import { useSnapshot } from "valtio";
import { i18nStore } from "@/store/i18n";
import { formatDateTime, levelColor, statusColor } from "./server-log.logic";
import type { ServerLogGroup } from "./server-log.types";
import { useServerLogText } from "./use-server-log-text";

interface Props {
	rows: ServerLogGroup[];
	total: number;
	page: number;
	pageSize: number;
	onPage: (page: number) => void;
	onSelect: (id: string) => void;
}

/** Tabel grup log terpaginasi; klik baris membuka detail. */
export function ServerLogTable({
	rows,
	total,
	page,
	pageSize,
	onPage,
	onSelect,
}: Props) {
	const t = useServerLogText();
	const { lang } = useSnapshot(i18nStore);
	const locale = lang === "en" ? "en-US" : "id-ID";
	const pages = Math.max(1, Math.ceil(total / pageSize));

	if (rows.length === 0) {
		return (
			<Text c="dimmed" ta="center" py="xl">
				{t.empty}
			</Text>
		);
	}

	return (
		<>
			<Table.ScrollContainer minWidth={720}>
				<Table highlightOnHover verticalSpacing="xs">
					<Table.Thead>
						<Table.Tr>
							<Table.Th>{t.columns.level}</Table.Th>
							<Table.Th>{t.columns.module}</Table.Th>
							<Table.Th>{t.columns.title}</Table.Th>
							<Table.Th ta="right">{t.columns.count}</Table.Th>
							<Table.Th>{t.columns.lastSeen}</Table.Th>
							<Table.Th>{t.columns.status}</Table.Th>
						</Table.Tr>
					</Table.Thead>
					<Table.Tbody>
						{rows.map((g) => (
							<Table.Tr
								key={g.id}
								onClick={() => onSelect(g.id)}
								style={{ cursor: "pointer" }}
							>
								<Table.Td>
									<Badge color={levelColor(g.level)} variant="light">
										{t.levels[g.level] ?? g.level}
									</Badge>
								</Table.Td>
								<Table.Td>
									<Text size="sm" ff="monospace">
										{g.module}
									</Text>
								</Table.Td>
								<Table.Td maw={420}>
									<Text size="sm" lineClamp={2}>
										{g.title}
									</Text>
								</Table.Td>
								<Table.Td ta="right">{g.count.toLocaleString(locale)}</Table.Td>
								<Table.Td>
									<Text size="xs" c="dimmed">
										{formatDateTime(g.lastSeenAt, locale)}
									</Text>
								</Table.Td>
								<Table.Td>
									<Badge color={statusColor(g.status)} variant="dot">
										{t.statuses[g.status] ?? g.status}
									</Badge>
								</Table.Td>
							</Table.Tr>
						))}
					</Table.Tbody>
				</Table>
			</Table.ScrollContainer>
			{pages > 1 && (
				<Pagination mt="md" total={pages} value={page} onChange={onPage} />
			)}
		</>
	);
}
